import {Injectable, Inject} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {VisitPlanHttpService} from "./visit-plan-http.service";
import {VisitPlanModel} from "../../models/visit-plan.model";
import {
  VisitPlanActions,
  VisitPlanAPiActions,
  VisitPlanChannelActions
} from "../state/visit-plan-state/visit-plan.actions";
import {visitPlanFeature} from "../state/visit-plan-state/visit-plan.state";
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";
import {VisitScheduleResourceModel} from "../../models/visit-schedule-resource.model";
import {VisitScheduleResourceHolderExt} from "../../models/visit-schedule-resource-holder-ext.model";
import {VisitScheduleResourcesParamsModel} from "../../models/visit-schedule-params.model";
import {VisitScheduleModel} from "../../models/visit-schedule.model";
import {VisitScheduleDayModel} from "../../models/visit-schedule-day.model";
import {VisitScheduleOptimizationSettings} from "../../models/visit-schedule-optimization-config.model";
import {VisitScheduleAPiActions} from "../state/visit-plan-schedule-state/visit-plan-schedule.actions";
import {VisitScheduleResourceHoldersParamsModel} from "../../models/visit-schedule-resource-holders-params.model";
import {ResourceHoldersPaginationDataModel} from "../../models/resource-holders-pagination-data.model";
import {VisitScheduleAgentImportOptionModel} from "../../models/visit-schedule-agent-import-option.model";
import {ModelListWithCursor} from "../../../commons/angular/models/model-list-with-cursor.model";
import {VisitPlanExternalAssignRequests} from "../../models/visit-plan-create-external-resource-holders-payload.model";
import {VisitPlanFilterModel} from "../../models/visit-plan-filter.model";

@Injectable()
export class VisitPlanFacadeService {

  constructor(@Inject(Store) private store: Store,
              @Inject(VisitPlanHttpService) private visitPlanHttpService: VisitPlanHttpService, @Inject(CommonsFacadeService) private commonsFacadeService: CommonsFacadeService) {
  }

  createOne(visitPlan: VisitPlanModel): Observable<VisitPlanModel> {
    return this.visitPlanHttpService.createVisitPlan(visitPlan);
  }

  updateVisitPlanName(visitPlan: VisitPlanModel): Observable<VisitPlanModel> {
    return this.visitPlanHttpService.updateVisitPlan(visitPlan).pipe(
      tap((updatedVisitPlan: VisitPlanModel) => {
        // Update the store with the updated visit plan
        this.store.dispatch(VisitPlanActions.setVisitPlan({visitPlan: updatedVisitPlan}));
      })
    );
  }

  getResourceHolders(visitPlanId: string, visitScheduleId: string, params: VisitScheduleResourceHoldersParamsModel): Observable<ResourceHoldersPaginationDataModel> {
    return this.visitPlanHttpService.getResourceHolders(visitPlanId, visitScheduleId, params);
  }

  getResourceHoldersWithCursor(visitPlanId: string, visitScheduleId: string, params: any): Observable<{
    list: VisitScheduleResourceHolderExt[],
    cursor?: string
  }> {
    return this.visitPlanHttpService.getResourceHoldersWithCursor(visitPlanId, visitScheduleId, params);
  }

  getExternalAgentsResourceHoldersCursor(visitPlanId: string, visitScheduleId: string, params: any): Observable<{
    list: VisitScheduleAgentImportOptionModel[],
    cursor?: string
  }> {
    return this.visitPlanHttpService.getExternalAgentsResourceHoldersCursor(visitPlanId, visitScheduleId, params);
  }

  importExternalAgentsResourceHolders(visitPlanId: string, visitScheduleId: string, agentIds: string[]): Observable<VisitScheduleResourceHolderExt[]> {
    return this.visitPlanHttpService.importExternalAgentsResourceHolders(visitPlanId, visitScheduleId, agentIds);
  }

  importExternalAgentsResourceHoldersDesignated(visitPlanId: string, visitScheduleId: string, payload: VisitPlanExternalAssignRequests[]): Observable<VisitScheduleResourceHolderExt[]> {
    return this.visitPlanHttpService.importExternalAgentsResourceHoldersDesignated(visitPlanId, visitScheduleId, payload);
  }

  // deleteOne(visitPlanId: string): any {
  //   return this.visitPlanHttpService.deleteVisitPlan(visitPlanId)
  // }

  deleteOne(id: string, shouldMask?: boolean): void {
    this.store.dispatch(VisitPlanAPiActions.deleteVisitPlan({visitPlanId: id, force: false}));
  }

  restoreOne(id: string, shouldMask?: boolean): void {
    this.store.dispatch(VisitPlanAPiActions.restoreVisitPlan({visitPlanId: id}));
  }


  // getMany(params?: any): Observable<VisitPlanModel[]> {
  //   return this.visitPlanHttpService.getVisitPlans(params).pipe(
  //     map(response => response.content)
  //   )
  // }

  getMany(filter: VisitPlanFilterModel, params?: any, isSubscribeToChannel?: boolean): void {
    this.store.dispatch(VisitPlanAPiActions.getVisitPlans({filter, params, isSubscribeToChannel}));
  }

  queryVisitPlansCursor(filter: VisitPlanFilterModel, params?: any): Observable<ModelListWithCursor<VisitPlanModel>> {
    return this.visitPlanHttpService.queryVisitPlansCursor(filter, params);
  }

  setOne(visitPlan: VisitPlanModel, isSubscribeToChannel?: boolean): void {
    this.store.dispatch(VisitPlanActions.setVisitPlan({visitPlan, isSubscribeToChannel}));
  }

  getVisitPlan(visitPlanId: string): Observable<VisitPlanModel> {
    return this.visitPlanHttpService.getVisitPlan(visitPlanId);
  }

  getOne(visitPlanId: string, isSubscribeToChannel?: boolean): void {
    this.store.dispatch(VisitPlanAPiActions.getVisitPlan({visitPlanId, isSubscribeToChannel: isSubscribeToChannel}));
  }

  selectOne(id: string) {
    return this.store.select(visitPlanFeature.selectVisitPlan(id));
  }

  selectAll(): Observable<VisitPlanModel[]> {
    return this.store.select(visitPlanFeature.selectVisitPlans());
  }

  subscribeToChannels(): void {
    this.store.dispatch(VisitPlanChannelActions.subscribe());
  }

  unsubscribeFromChannels(): void {
    this.store.dispatch(VisitPlanChannelActions.unsubscribe());
  }

  clearStore() {
    this.store.dispatch(VisitPlanActions.clearStore());
  }

  // VISIT SCHEDULE
  initVisitSchedule(visitPlanId: string, visitScheduleConfig: VisitScheduleOptimizationSettings): void {
    this.store.dispatch(VisitScheduleAPiActions.initVisitSchedule({visitPlanId, visitScheduleConfig}));
  }

  getVisitSchedule(visitPlanId: string, visitScheduleId: string): Observable<VisitScheduleModel> {
    return this.visitPlanHttpService.getVisitSchedule(visitPlanId, visitScheduleId);
  }

  getVisitScheduleDaysData(visitPlanId: string, visitScheduleId: string, params): Observable<{
    list: VisitScheduleDayModel[],
    cursor?: string
  }> {
    return this.visitPlanHttpService.getVisitScheduleDaysData(visitPlanId, visitScheduleId, params);
  }

  getResources(visitPlanId: string, visitScheduleId: string, query: VisitScheduleResourcesParamsModel): Observable<{
    list: VisitScheduleResourceModel[],
    cursor?: string
  }> {
    return this.visitPlanHttpService.getResources(visitPlanId, visitScheduleId, query);
  }

  syncVisitPlanDeliveryDetails(visitPlanId: string) {
    return this.visitPlanHttpService.syncVisitPlanDeliveryDetails(visitPlanId);
  }

}
